"use client";

import { useState } from "react";
import { ContainerLoader } from "../loaders/ContainerLoader";
import { UpsellBodyText } from "./UpsellBodyText";
import { UpsellFormDropdown } from "./UpsellFormDropdown";
import { UpsellFormInput } from "./UpsellFormInput";
import { UpsellHeader } from "./UpsellHeader";
import Image from "next/image";

export interface IUpsellForm {
  messages: {
    errorText: string;
    companyName: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    zipCode: string;
    submitButton: string;
    submitThankYou: string;
    submitBody: string;
    formHeader: string;
    formBody: string;
  };
  dropdownMessages: {
    industry: string;
    lender: string;
    multifamilyPropertyManager: string;
    broker: string;
    retailer: string;
    serviceProvider: string;
    ownerAndDeveloper: string;
    appraiser: string;
  };
}

export const UpsellForm = (props: IUpsellForm) => {
  const { messages, dropdownMessages } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const handleFormSubmit = async (e: {
    preventDefault: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    target: any;
  }) => {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);

    const firstName = formData.get("firstName");
    const lastName = formData.get("lastName");
    const industry = selectedOption;
    const company = formData.get("company");
    const emailAddress = formData.get("email");
    const phone = formData.get("phone");
    const zipCode = formData.get("zipCode");

    setIsError(false);
    setIsLoading(true);

    fetch("/api/upsell/lead", {
      method: "POST",
      body: JSON.stringify({
        firstName,
        lastName,
        company,
        emailAddress,
        phone,
        zipCode,
        sourceUrl: window.location.pathname,
        additionalData: {
          industry,
        },
      }),
    })
      .then((response) => {
        if (response.status === 200) {
          setIsSuccessful(true);
        }
        throw new Error("API post failed");
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [selectedOption, setSelectedOption] = useState("");

  const handleFirstNameChange = (e: { target: { value: string } }) => {
    setFirstName(e.target.value.trim());
  };

  const handleLastNameChange = (e: { target: { value: string } }) => {
    setLastName(e.target.value.trim());
  };

  const handleCompanyNameChange = (e: { target: { value: string } }) => {
    setCompanyName(e.target.value.trim());
  };

  const handleEmailAddressChange = (e: { target: { value: string } }) => {
    setEmailAddress(e.target.value.trim());
  };

  const handlePhoneNumberChange = (e: { target: { value: string } }) => {
    setPhoneNumber(e.target.value.trim());
  };

  const handlePostalCodeChange = (e: { target: { value: string } }) => {
    setPostalCode(e.target.value.trim());
  };

  const handleDropdownChange = (value: string) => {
    const selectedValue = value;
    if (selectedValue !== "") {
      setSelectedOption(selectedValue);
    }
  };

  const isSubmitDisabled = !(
    firstName &&
    lastName &&
    companyName &&
    emailAddress &&
    phoneNumber &&
    postalCode &&
    selectedOption &&
    selectedOption !== "Secteur d'activité"
  );

  return !isSuccessful ? (
    <>
      {isLoading && <ContainerLoader className="rounded-xl" />}
      <div className="grid gap-y-3">
        <UpsellHeader text={messages.formHeader} />
        <UpsellBodyText text={messages.formBody} />
      </div>
      <form
        automation-id="upsell-message-form"
        className="flex flex-col gap-y-4 font-sans text-p-16 leading-[23px]"
        onSubmit={handleFormSubmit}
      >
        <div automation-id="upsell-message-form-input-first-name">
          <UpsellFormInput
            type={"text"}
            name={"firstName"}
            placeholder={messages.firstName + "*"}
            onChangeHandler={handleFirstNameChange}
          />
        </div>
        <div automation-id="upsell-message-form-input-last-name">
          <UpsellFormInput
            type={"text"}
            name={"lastName"}
            placeholder={messages.lastName + "*"}
            onChangeHandler={handleLastNameChange}
          />
        </div>
        <div automation-id="upsell-message-form-input-company">
          <UpsellFormInput
            type={"text"}
            name={"company"}
            placeholder={messages.companyName + "*"}
            onChangeHandler={handleCompanyNameChange}
          />
        </div>
        <div automation-id="upsell-message-form-dropdown-industry">
          <UpsellFormDropdown
            messages={{
              industry: dropdownMessages.industry,
              lender: dropdownMessages.lender,
              multifamilyPropertyManager:
                dropdownMessages.multifamilyPropertyManager,
              broker: dropdownMessages.broker,
              retailer: dropdownMessages.retailer,
              serviceProvider: dropdownMessages.serviceProvider,
              ownerAndDeveloper: dropdownMessages.ownerAndDeveloper,
              appraiser: dropdownMessages.appraiser,
            }}
            value={selectedOption}
            onChangeHandler={handleDropdownChange}
          />
        </div>
        <div automation-id="upsell-message-form-input-email">
          <UpsellFormInput
            type={"email"}
            name={"email"}
            placeholder={messages.email + "*"}
            onChangeHandler={handleEmailAddressChange}
          />
        </div>
        <div className="flex flex-col gap-y-4 lg:flex-row lg:gap-x-4">
          <div automation-id="upsell-message-form-input-phone-number">
            <UpsellFormInput
              type={"text"}
              name={"phone"}
              placeholder={messages.phone + "*"}
              onChangeHandler={handlePhoneNumberChange}
            />
          </div>
          <div automation-id="upsell-message-form-input-postal-code">
            <UpsellFormInput
              type={"text"}
              name={"zipCode"}
              placeholder={messages.zipCode + "*"}
              onChangeHandler={handlePostalCodeChange}
            />
          </div>
        </div>
        <div>
          <button
            automation-id="upsell-message-form-submit-bitton"
            type="submit"
            className={
              "w-full rounded-sm border px-2 py-1 font-sans text-p-16 font-medium" +
              (isSubmitDisabled ? " bg-white" : " bg-primary text-white")
            }
            disabled={isSubmitDisabled}
          >
            {messages.submitButton}
          </button>
          {isError && (
            <p
              id="errorText"
              className="text-state-danger font-sans text-p-16 font-medium"
            >
              {messages.errorText}
            </p>
          )}
        </div>
      </form>
    </>
  ) : (
    <div
      automation-id="upsell-message-form-submit-container"
      className="flex w-full flex-col items-center gap-y-4 lg:w-[390px]"
    >
      <div
        automation-id="upsell-message-form-submit-text"
        className="flex flex-col items-start gap-y-3"
      >
        <UpsellHeader text={messages.submitThankYou} />
        <UpsellBodyText text={messages.submitBody} />
      </div>
      <Image
        automation-id="upsell-message-logo"
        src="/logo-bi-nobaseline.svg"
        alt="Business Immo Logo"
        width={104}
        height={32}
      />
    </div>
  );
};
